/**
 * Pure business-logic helpers for the permission-matrix grant editor.
 *
 * Operates on the real `RoleScopeRow` shape from `@ooh/contracts` so these
 * helpers work unchanged once a real screen wires role-scope queries and
 * mutations to `GrantRow` / `PermissionGrantList`.
 *
 * Hook-free by design (no `useTranslation`) so they can run outside a
 * component (e.g. inside `useMemo`, or from the mockup screen's own state
 * derivations). The scope-chip summary labels below use hardcoded Polish —
 * follow-up: thread a `t()` function through them the way
 * `formatReservationStatus` does in `reservations/lib/formatters.ts`, once
 * this editor is wired to a real route.
 */
import { PERMISSION_BUNDLES } from '@ooh/contracts';
import type { RoleScopeRow, ScopeOwnership, OfferDepartment } from '@ooh/contracts';

/** Base scope shape for a newly-granted permission (wildcard on every dimension). */
const EMPTY_GRANT_BASE = { mediaType: null, ownership: null, department: null } as const;

// ─── Grant cloning ────────────────────────────────────────────

/** Deep-clones a role-code -> grant-rows map (used to seed local/staged state from committed data). */
export function cloneGrants(
  source: Record<string, RoleScopeRow[]>,
): Record<string, RoleScopeRow[]> {
  const next: Record<string, RoleScopeRow[]> = {};
  for (const [roleCode, grants] of Object.entries(source)) {
    next[roleCode] = grants.map((grant) => ({ ...grant }));
  }
  return next;
}

// ─── Staged-edit diffing (batch save/discard) ───────────────────

export function getGrantsForPermission(
  grants: RoleScopeRow[],
  permissionCode: string,
): RoleScopeRow[] {
  return grants.filter((g) => g.permissionCode === permissionCode);
}

/** Canonical, order-independent signature for a permission's scope rows. */
export function serializeGrantSet(grants: RoleScopeRow[]): string {
  return grants
    .map((g) => `${g.mediaType ?? ''}|${g.ownership ?? ''}|${g.department ?? ''}`)
    .sort()
    .join(';');
}

/** Number of permission rows whose scope differs between committed and staged grants. */
export function countChangedPermissions(committed: RoleScopeRow[], staged: RoleScopeRow[]): number {
  const codes = new Set([
    ...committed.map((g) => g.permissionCode),
    ...staged.map((g) => g.permissionCode),
  ]);
  let count = 0;
  for (const code of codes) {
    const before = serializeGrantSet(getGrantsForPermission(committed, code));
    const after = serializeGrantSet(getGrantsForPermission(staged, code));
    if (before !== after) count += 1;
  }
  return count;
}

// ─── Grant row split/merge rules ─────────────────────────────
// Row-mutation logic for the batch-save grant editor. `applyGrantUpdate`
// isolates a permission's rows from the rest of a role's grants, applies an
// updater, and merges the result back in. The `with*`/`grant*`/`revoke*`
// functions below are the updaters themselves — the actual scope-mutation
// rules a caller (e.g. the permission-matrix screen) wires up to UI events.

/**
 * Splits `base` into the rows for `permissionCode` and everything else,
 * applies `updater` to the matching rows, and merges the result back with
 * the untouched rows. Callers own persistence (e.g. staged-state `setState`).
 */
export function applyGrantUpdate(
  base: RoleScopeRow[],
  permissionCode: string,
  updater: (grants: RoleScopeRow[]) => RoleScopeRow[],
): RoleScopeRow[] {
  const others = base.filter((g) => g.permissionCode !== permissionCode);
  const forThisPermission = base.filter((g) => g.permissionCode === permissionCode);
  const updated = updater(forThisPermission);
  return [...others, ...updated];
}

/** Grants a permission with a fresh wildcard-scope row. */
export function grantPermissionScope(permissionCode: string): RoleScopeRow[] {
  return [{ permissionCode, ...EMPTY_GRANT_BASE }];
}

/** Revokes a permission entirely (zero rows). */
export function revokePermissionScope(): RoleScopeRow[] {
  return [];
}

// ─── Scope-group rendering model ─────────────────────────────
// A permission's flat RoleScopeRow[] array is grouped into UI-level "scope
// groups" for the grant editor: rows sharing the same (ownership,
// department) pair collapse into one editable group with a combined
// media-type set. This lets one permission (e.g. supplier.view) hold several
// INDEPENDENT scope combinations at once — e.g. "large format + mural" at
// RW+Broker ownership, and "billboard" restricted to RW only — each with its
// own add/remove/edit affordance, instead of forcing one ownership/
// department pair across every row. See `GrantRow` / `ScopeGroupRow` for the
// rendering side.

/**
 * Groups a permission's scope rows for the grant editor — see module doc
 * above. Preserves first-appearance order so groups render deterministically
 * as the underlying array mutates.
 */
/** Canonical (ownership, department) key for a scope row or a scope group's first row — the identity `groupGrantsByScope` collapses rows by. */
function scopeGroupKey(row: Pick<RoleScopeRow, 'ownership' | 'department'> | undefined): string {
  return `${row?.ownership ?? ''}|${row?.department ?? ''}`;
}

export function groupGrantsByScope(grants: RoleScopeRow[]): RoleScopeRow[][] {
  const groups: RoleScopeRow[][] = [];
  const indexByKey = new Map<string, number>();
  for (const grant of grants) {
    const key = scopeGroupKey(grant);
    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, groups.length);
      groups.push([grant]);
    } else {
      groups[existingIndex]?.push(grant);
    }
  }
  return groups;
}

/** Rebuilds a permission's flat row array with one group's rows replaced, keeping the other groups' relative order. */
function replaceScopeGroup(
  grants: RoleScopeRow[],
  groupIndex: number,
  nextGroupRows: RoleScopeRow[],
): RoleScopeRow[] {
  return groupGrantsByScope(grants).flatMap((group, i) =>
    i === groupIndex ? nextGroupRows : group,
  );
}

/** Rewrites one scope group's media-type selection. `null` = wildcard (all media types) -> single row. An empty array = "no types picked yet" -> zero rows for that group. */
export function withGroupMediaTypeScope(
  grants: RoleScopeRow[],
  permissionCode: string,
  groupIndex: number,
  mediaTypeCodes: string[] | null,
): RoleScopeRow[] {
  const groups = groupGrantsByScope(grants);
  const base = groups[groupIndex]?.[0] ?? { permissionCode, ...EMPTY_GRANT_BASE };
  const nextRows =
    mediaTypeCodes === null
      ? [{ ...base, mediaType: null }]
      : mediaTypeCodes.map((mt) => ({ ...base, mediaType: mt }));
  return replaceScopeGroup(grants, groupIndex, nextRows);
}

/**
 * Whether retargeting `groups[groupIndex]` to `nextKey` would collide with
 * another, still-independent group's (ownership, department) key — the
 * shared collision-detection core for both the mutating no-op guards
 * (`withGroupOwnershipScope`/`withGroupDepartmentScope`) and the
 * UI-disablement queries (`ownershipOptionCollides`/
 * `departmentOptionCollides`) below, so the two can never diverge.
 */
function collidesWithSiblingGroup(
  groups: RoleScopeRow[][],
  groupIndex: number,
  nextKey: Pick<RoleScopeRow, 'ownership' | 'department'>,
): boolean {
  const key = scopeGroupKey(nextKey);
  return groups.some((group, i) => i !== groupIndex && scopeGroupKey(group[0]) === key);
}

/**
 * Rewrites one scope group's ownership dimension, across every row in that
 * group only — other groups are untouched. Returns `grants` unchanged
 * (no-op) if the new (ownership, department) key would collide with another,
 * still-independent group: applying the edit would make `groupGrantsByScope`
 * re-collapse the two groups into one on the next render, silently merging
 * their media-type sets. Callers MUST also disable the colliding option in
 * the UI (see `ownershipOptionCollides`) rather than relying on this no-op
 * alone, which is invisible to the admin.
 */
export function withGroupOwnershipScope(
  grants: RoleScopeRow[],
  groupIndex: number,
  ownership: ScopeOwnership | null,
): RoleScopeRow[] {
  const groups = groupGrantsByScope(grants);
  const current = groups[groupIndex];
  if (current === undefined) return grants;
  const nextKey = { ownership, department: current[0]?.department ?? null };
  if (collidesWithSiblingGroup(groups, groupIndex, nextKey)) return grants;
  const nextRows = current.map((g) => ({ ...g, ownership }));
  return replaceScopeGroup(grants, groupIndex, nextRows);
}

/**
 * Rewrites one scope group's department dimension, across every row in that
 * group only — other groups are untouched. Returns `grants` unchanged
 * (no-op) if the new (ownership, department) key would collide with another,
 * still-independent group — see `withGroupOwnershipScope` for why, and
 * `departmentOptionCollides` for the matching UI-disablement query.
 */
export function withGroupDepartmentScope(
  grants: RoleScopeRow[],
  groupIndex: number,
  department: OfferDepartment | null,
): RoleScopeRow[] {
  const groups = groupGrantsByScope(grants);
  const current = groups[groupIndex];
  if (current === undefined) return grants;
  const nextKey = { ownership: current[0]?.ownership ?? null, department };
  if (collidesWithSiblingGroup(groups, groupIndex, nextKey)) return grants;
  const nextRows = current.map((g) => ({ ...g, department }));
  return replaceScopeGroup(grants, groupIndex, nextRows);
}

/**
 * Whether picking `ownership` for `groupIndex` would collide with a sibling
 * scope group — i.e. `withGroupOwnershipScope` would silently no-op for this
 * value. Drives the ownership `RadioGroupItem`s' disabled state in
 * `ScopeGroupRow` so a colliding choice renders visibly unavailable (with an
 * explanatory tooltip) instead of looking pickable and silently doing
 * nothing on click.
 */
export function ownershipOptionCollides(
  grants: RoleScopeRow[],
  groupIndex: number,
  ownership: ScopeOwnership | null,
): boolean {
  const groups = groupGrantsByScope(grants);
  const current = groups[groupIndex];
  if (current === undefined) return false;
  const nextKey = { ownership, department: current[0]?.department ?? null };
  return collidesWithSiblingGroup(groups, groupIndex, nextKey);
}

/**
 * Whether picking `department` for `groupIndex` would collide with a
 * sibling scope group — the department-dimension counterpart of
 * `ownershipOptionCollides`, see there for why.
 */
export function departmentOptionCollides(
  grants: RoleScopeRow[],
  groupIndex: number,
  department: OfferDepartment | null,
): boolean {
  const groups = groupGrantsByScope(grants);
  const current = groups[groupIndex];
  if (current === undefined) return false;
  const nextKey = { ownership: current[0]?.ownership ?? null, department };
  return collidesWithSiblingGroup(groups, groupIndex, nextKey);
}

/** Removes one scope group entirely (all of its rows) — the group's own "remove" affordance. */
export function removeScopeGroup(grants: RoleScopeRow[], groupIndex: number): RoleScopeRow[] {
  return groupGrantsByScope(grants)
    .filter((_, i) => i !== groupIndex)
    .flat();
}

/**
 * Every ownership scope value in display order (`null` = wildcard/"both").
 * Exported so `ScopeGroupRow`'s ownership radio options render from the same
 * list `nextAvailableScopeKey`'s candidate search uses below — a single
 * source of truth the two can't silently drift apart on.
 */
export const OWNERSHIP_CANDIDATES: readonly (ScopeOwnership | null)[] = [null, 'RW', 'BROKER'];
/** Department-dimension counterpart of `OWNERSHIP_CANDIDATES` — see there for why it's exported. */
export const DEPARTMENT_CANDIDATES: readonly (OfferDepartment | null)[] = [
  null,
  'sales',
  'planners',
  'new_products',
];

/**
 * Finds an (ownership, department) key not already used by any of the
 * permission's existing scope groups — the candidate a new "Dodaj zakres"
 * group would use. Enumerates department options only when
 * `departmentScopeEditable` is true (see `allowDepartmentScope` in
 * `role-codes.ts`); for permissions where the department dimension is a
 * fixed wildcard, only the ownership dimension can distinguish groups.
 * Returns `null` once every distinct key in that space is already used —
 * callers MUST treat that as "cannot add another group" rather than falling
 * back to a used key, which would collapse the new row into (and silently
 * widen) an existing group instead of creating a distinct one.
 */
function nextAvailableScopeKey(
  groups: RoleScopeRow[][],
  departmentScopeEditable: boolean,
): { ownership: ScopeOwnership | null; department: OfferDepartment | null } | null {
  const used = new Set(groups.map((group) => scopeGroupKey(group[0])));
  const departmentCandidates = departmentScopeEditable ? DEPARTMENT_CANDIDATES : [null];
  for (const department of departmentCandidates) {
    for (const ownership of OWNERSHIP_CANDIDATES) {
      if (!used.has(scopeGroupKey({ ownership, department }))) return { ownership, department };
    }
  }
  return null;
}

/**
 * Whether a permission still has a distinct (ownership, department) key
 * available for another independent scope group — drives the "Dodaj zakres"
 * affordance's disabled state so the admin can't trigger the silent-widen
 * bug `addScopeGroup` guards against below.
 */
export function canAddScopeGroup(
  grants: RoleScopeRow[],
  departmentScopeEditable: boolean,
): boolean {
  return nextAvailableScopeKey(groupGrantsByScope(grants), departmentScopeEditable) !== null;
}

/**
 * Adds a new independent scope group ("Dodaj zakres") to an already-granted
 * permission, using the next key `nextAvailableScopeKey` finds. Returns
 * `grants` unchanged (no-op) once every distinct key is already used —
 * callers MUST gate the affordance with `canAddScopeGroup` so this no-op
 * path is unreachable from the UI rather than silently doing nothing on click.
 */
export function addScopeGroup(
  grants: RoleScopeRow[],
  permissionCode: string,
  departmentScopeEditable: boolean,
): RoleScopeRow[] {
  const nextKey = nextAvailableScopeKey(groupGrantsByScope(grants), departmentScopeEditable);
  if (nextKey === null) return grants;
  return [...grants, { permissionCode, mediaType: null, ...nextKey }];
}

// ─── Permission-bundle cascade ────────────────────────────────
// Some permissions are functionally coupled by a directional rule (e.g.
// media_unit.block_availability requires supplier.view so the import preview
// screen's supplier dropdown isn't permanently empty — see PERMISSION_BUNDLES
// in @ooh/contracts). Granting a rule's `trigger` cascades a grant of its
// `requires` codes; revoking a `requires` code cascades a revoke of any
// `trigger` that currently depends on it. The reverse directions are NOT
// cascaded: revoking a `trigger` leaves its `requires` codes untouched, and
// granting a `requires` code alone never auto-grants a `trigger` — a role can
// hold e.g. supplier.view without block_availability, and that's valid.

/** Codes that must also be granted whenever `permissionCode` is granted (it's a rule's `trigger`), or `[]`. */
function permissionsRequiredWhenGranting(permissionCode: string): readonly string[] {
  const rule = PERMISSION_BUNDLES.find((r) => r.trigger === permissionCode);
  return rule ? rule.requires : [];
}

/**
 * Bundle `trigger`s that require `permissionCode`. Revoking `permissionCode`
 * while one of these triggers is still granted would leave that trigger
 * without a permission it requires, so the revoke cascade also revokes it.
 */
function triggersThatRequire(permissionCode: string): readonly string[] {
  return PERMISSION_BUNDLES.filter((rule) =>
    (rule.requires as readonly string[]).includes(permissionCode),
  ).map((rule) => rule.trigger);
}

/**
 * Every other permission code connected to `permissionCode` via a
 * `PERMISSION_BUNDLES` rule, in either direction. Exported (not just used
 * internally by the grant/revoke cascades below) so `PermissionGrantList` can
 * compute which currently-granted permissions are paired with a given row,
 * to power the "required together with X" inline notice in `GrantRow` — that
 * notice is informative regardless of which side is granted, even though the
 * grant/revoke cascade enforcement below is directional.
 */
export function bundlePartnersOf(permissionCode: string): readonly string[] {
  return Array.from(
    new Set([
      ...permissionsRequiredWhenGranting(permissionCode),
      ...triggersThatRequire(permissionCode),
    ]),
  );
}

/** Loose shape of i18next's `t` — narrow enough to use outside a component, without importing react-i18next into this hook-free file. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Translated display name for a permission code, falling back to the raw
 * code when untranslated. Single source of truth for this lookup — shared by
 * the bundle-conflict save-error toast (`use-grant-staging.ts`) and the
 * inline "required together with X" notice (`GrantRow`'s `BundleNotice`) so
 * they can't silently diverge.
 */
export function permissionDisplayName(t: TranslateFn, code: string): string {
  return t(`permissionMatrix.permissions.${code}.name`, { defaultValue: code });
}

/**
 * Grants `permissionCode` with a wildcard-scope row, and — only when
 * `permissionCode` is a bundle rule's `trigger` — atomically grants every
 * `requires` code that isn't already granted (also with a fresh
 * wildcard-scope row). An already-granted required code's existing scope is
 * left untouched. Granting a `requires` code that isn't itself a trigger
 * behaves like a plain grant (no cascade).
 */
export function grantPermissionWithBundle(
  grants: RoleScopeRow[],
  permissionCode: string,
): RoleScopeRow[] {
  let next = applyGrantUpdate(grants, permissionCode, () => grantPermissionScope(permissionCode));
  for (const requiredCode of permissionsRequiredWhenGranting(permissionCode)) {
    const alreadyGranted = next.some((g) => g.permissionCode === requiredCode);
    if (!alreadyGranted) {
      next = applyGrantUpdate(next, requiredCode, () => grantPermissionScope(requiredCode));
    }
  }
  return next;
}

/**
 * Revokes `permissionCode`, and cascades the revoke to every bundle
 * `trigger` that currently requires it (so no trigger is left without a
 * permission it requires). Revoking a `trigger` itself does NOT cascade to
 * its `requires` codes — those stay valid on their own (e.g. revoking
 * `media_unit.block_availability` never touches `supplier.view`). Cascading
 * (rather than blocking the individual revoke with an inline explanation)
 * keeps the staged-diff editor's mental model simple and needs no new
 * "blocked toggle" UI state, while still guaranteeing a trigger is never left
 * without a permission it requires.
 */
export function revokePermissionWithBundle(
  grants: RoleScopeRow[],
  permissionCode: string,
): RoleScopeRow[] {
  let next = applyGrantUpdate(grants, permissionCode, () => revokePermissionScope());
  for (const dependentTrigger of triggersThatRequire(permissionCode)) {
    const stillGranted = next.some((g) => g.permissionCode === dependentTrigger);
    if (stillGranted) {
      next = applyGrantUpdate(next, dependentTrigger, () => revokePermissionScope());
    }
  }
  return next;
}

/**
 * Adds a scope row/group for `permissionCode` — the "Dodaj zakres" affordance
 * and the initial-grant "Nadaj" affordance both call this. When the
 * permission isn't granted yet, this is equivalent to a plain (bundle-aware)
 * grant, cascading its bundle partner via `grantPermissionWithBundle`. When
 * it already has one or more scope groups, this appends an additional
 * INDEPENDENT group instead of touching the existing ones — a no-op once
 * `canAddScopeGroup` would report exhaustion (see `addScopeGroup`).
 */
export function addPermissionScopeGroup(
  grants: RoleScopeRow[],
  permissionCode: string,
  departmentScopeEditable: boolean,
): RoleScopeRow[] {
  const existing = getGrantsForPermission(grants, permissionCode);
  if (existing.length === 0) return grantPermissionWithBundle(grants, permissionCode);
  return applyGrantUpdate(grants, permissionCode, (g) =>
    addScopeGroup(g, permissionCode, departmentScopeEditable),
  );
}

/**
 * Removes one scope group from `permissionCode` — a scope row's own "x"
 * affordance. Removing the last remaining group revokes the permission
 * entirely, cascading to any bundle `trigger` that requires it (see
 * `revokePermissionWithBundle`); otherwise only that group's rows are
 * dropped and the permission stays granted via its other, independent scope
 * groups.
 */
export function removePermissionScopeGroup(
  grants: RoleScopeRow[],
  permissionCode: string,
  groupIndex: number,
): RoleScopeRow[] {
  const existing = getGrantsForPermission(grants, permissionCode);
  const groupCount = groupGrantsByScope(existing).length;
  if (groupCount <= 1) return revokePermissionWithBundle(grants, permissionCode);
  return applyGrantUpdate(grants, permissionCode, (g) => removeScopeGroup(g, groupIndex));
}

// ─── Scope-chip summary labels ───────────────────────────────

/**
 * Summarizes a permission's media-type scope rows into a compact chip label.
 * @param mediaTypeLabel - Display-label lookup for a media type code. Media
 *   type labels are data-driven (not static UI chrome), so the caller
 *   supplies the lookup rather than this helper importing a fixed list.
 */
export function summarizeMediaTypeLabel(
  grants: RoleScopeRow[],
  mediaTypeLabel: (code: string) => string,
): string {
  if (grants.length === 0) return '—';
  if (grants.some((grant) => grant.mediaType === null)) return 'Wszystkie typy nośników';
  const types = Array.from(
    new Set(grants.map((grant) => grant.mediaType).filter((v): v is string => v !== null)),
  );
  if (types.length <= 2) return types.map(mediaTypeLabel).join(' + ');
  return `${types.length} typy`;
}

/**
 * Summarizes a permission's ownership scope rows into a compact chip label.
 * @param ownershipLabel - Display-label lookup for a single ownership value,
 *   mirroring `summarizeMediaTypeLabel`'s `mediaTypeLabel` param — the
 *   caller supplies the translated lookup instead of this helper hardcoding one.
 */
export function summarizeOwnershipLabel(
  grants: RoleScopeRow[],
  ownershipLabel: (value: ScopeOwnership) => string,
): string {
  if (grants.length === 0) return '—';
  const values = new Set(grants.map((grant) => grant.ownership));
  if (values.size === 1) {
    const only = values.values().next().value as ScopeOwnership | null;
    return only === null ? 'RW + Broker' : ownershipLabel(only);
  }
  return 'RW + Broker';
}

/**
 * Summarizes a permission's department scope rows into a compact chip label.
 * @param departmentLabel - Display-label lookup for a single department value,
 *   mirroring `summarizeMediaTypeLabel`'s `mediaTypeLabel` param.
 */
export function summarizeDepartmentLabel(
  grants: RoleScopeRow[],
  departmentLabel: (value: OfferDepartment) => string,
): string {
  if (grants.length === 0) return '—';
  const values = new Set(grants.map((grant) => grant.department));
  if (values.size === 1) {
    const only = values.values().next().value as OfferDepartment | null;
    return only === null ? 'Wszystkie działy' : departmentLabel(only);
  }
  return 'Wszystkie działy';
}
